import React from "react";
import { createRoot } from "react-dom/client";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import ApiCall from "../../Apicall/ApiCall";
import { AUTH_API_BASE_URL } from "../../config/api-config";
import { getToken } from "../../Compo/utilis/getToken";
import ContractDocumentView from "../components/ds/ContractDocumentView";
import { resolveBuyerName } from "../components/ds/SellerProfileFlow";

// Downloads the contract as a PDF FILE that looks exactly like the contract view
// ("View contract" modal / ContractDocumentView) — the live, current rendering of the
// contract data — instead of the server-side pre-built ContractPdf template.

const A4_W_MM = 210;
const A4_H_MM = 297;
const MARGIN_MM = 10;

// Is this pixel row blank enough to cut a page on without slicing through text?
function isBlankRow(ctx, y, width) {
  const row = ctx.getImageData(0, y, width, 1).data;
  const [r0, g0, b0] = [row[0], row[1], row[2]];
  for (let x = 0; x < width; x += 3) {
    const i = x * 4;
    if (Math.abs(row[i] - r0) > 12 || Math.abs(row[i + 1] - g0) > 12 || Math.abs(row[i + 2] - b0) > 12) return false;
  }
  return true;
}

export async function downloadElementAsPdf(element, fileName) {
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch (_) {
      /* ignore */
    }
  }
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
    windowWidth: element.scrollWidth,
  });

  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const imgW = A4_W_MM - MARGIN_MM * 2;
  const pageContentH = A4_H_MM - MARGIN_MM * 2;
  const pxPerMm = canvas.width / imgW;
  const pagePx = Math.floor(pageContentH * pxPerMm);
  const ctx = canvas.getContext("2d", { willReadFrequently: true });

  let y = 0;
  let first = true;
  while (y < canvas.height) {
    let end = Math.min(y + pagePx, canvas.height);
    if (end < canvas.height) {
      // Prefer breaking on a blank row within the last ~10% of the page.
      const floor = end - Math.floor(pagePx * 0.1);
      for (let cut = end; cut > floor; cut--) {
        if (isBlankRow(ctx, cut, canvas.width)) {
          end = cut;
          break;
        }
      }
    }
    const sliceH = end - y;
    const slice = document.createElement("canvas");
    slice.width = canvas.width;
    slice.height = sliceH;
    slice.getContext("2d").drawImage(canvas, 0, y, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

    if (!first) pdf.addPage();
    pdf.addImage(slice.toDataURL("image/jpeg", 0.95), "JPEG", MARGIN_MM, MARGIN_MM, imgW, sliceH / pxPerMm);
    first = false;
    y = end;
  }
  pdf.save(fileName);
}

// Fetches the latest contract by id, renders the same ContractDocumentView off-screen,
// and downloads it as a PDF. Used by the transaction record's download icon.
export async function downloadContractPdfById({ t, id, fileName }) {
  const res = await ApiCall({ url: "/ContractMain/GetContractMainById", method: "GET", params: { Id: id } });
  const contractData = res?.data?.result || res?.result;
  if (!contractData) throw new Error("Contract not found");
  let buyerName = "";
  try {
    buyerName = await resolveBuyerName(contractData);
  } catch (_) {
    /* name is optional */
  }

  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  // Off-screen but laid out at a fixed A4-ish width so the capture is stable.
  host.style.cssText = "position:fixed;left:-10000px;top:0;width:820px;background:#fff;z-index:-1;";
  const inner = document.createElement("div");
  inner.className = "klargo-public ds-scope bg-transparent";
  host.appendChild(inner);
  document.body.appendChild(host);

  const root = createRoot(inner);
  try {
    root.render(
      React.createElement(ContractDocumentView, {
        t,
        contractData,
        buyerName,
        hideSourceNote: true,
        signaturesFooterKey: "agreement.signaturesFooterSigned",
        signaturesFooterBold: true,
      })
    );
    // let React commit + layout settle
    await new Promise((r) => setTimeout(r, 600));
    await downloadElementAsPdf(inner, fileName || `Klargo-Contract-${contractData.carInfoRegistrationNo || id}.pdf`);
  } finally {
    root.unmount();
    host.remove();
  }
}

// Downloads a file the API stores (never regenerated): the signed contract PDF of a deal
// whose signing has started, or the BankID evidence package. Parties only (server-checked).
async function downloadFromApi(path, fallbackName) {
  const token = getToken();
  const res = await fetch(`${AUTH_API_BASE_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const name = match ? decodeURIComponent(match[1]) : fallbackName;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const downloadStoredContractPdf = ({ id, lang }) =>
  downloadFromApi(`/ContractPdf/Download?id=${encodeURIComponent(id)}&lang=${lang === "en" ? "en" : "sv"}`, `Klargo-Contract-${id}.pdf`);

export const downloadEvidencePackage = (id) =>
  downloadFromApi(`/ContractEvidence/Export?id=${encodeURIComponent(id)}`, `Klargo-Evidence-${id}.zip`);
